/**
 * Maps operating-currency API rows (v2 mini serializer) to shapes consumed by
 * legacy CurrencyTable and the Vue 3 OperatingCurrenciesTable rebuild.
 */
export function resolveOperatingCurrencyStatus(row) {
  if (row != null && typeof row.status === "string" && row.status) {
    return row.status;
  }
  if (row?.active === false) {
    return "retired";
  }
  return "active";
}

function flagcdnFromCode(code) {
  const c = String(code || "")
    .trim()
    .toLowerCase();
  if (!c || c.length !== 2) return "";
  return `https://flagcdn.com/${c}.svg`;
}

function resolveFlagUrl(flagIcon, baseURL = "") {
  if (!flagIcon) return null;
  const s = String(flagIcon).trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://") || s.startsWith("data:")) {
    return s;
  }
  return `${baseURL}${s}`;
}

function resolveFlags(currency, baseURL = "") {
  const countries = Array.isArray(currency.countries) ? currency.countries : [];
  const fromCountries = countries
    .map((country) => {
      const fromIcon = resolveFlagUrl(country?.flag_icon, baseURL);
      if (fromIcon) return fromIcon;
      return flagcdnFromCode(country?.code) || null;
    })
    .filter(Boolean);

  if (fromCountries.length > 0) {
    return fromCountries;
  }

  if (Array.isArray(currency.flags) && currency.flags.length > 0) {
    return currency.flags.map((f) => resolveFlagUrl(f, baseURL)).filter(Boolean);
  }

  const fromIso = flagcdnFromCode(currency.iso_country_code);
  return fromIso ? [fromIso] : [];
}

export function mapOperatingCurrencyRow(currency, baseURL = "") {
  if (currency == null || typeof currency !== "object") {
    return currency;
  }

  const countries = Array.isArray(currency.countries) ? currency.countries : [];
  const name = currency.currency_name ?? currency.name ?? "";
  const code = currency.currency_code ?? currency.code ?? "";
  const symbol = currency.currency_symbol ?? currency.symbol ?? "";
  const status = resolveOperatingCurrencyStatus(currency);
  const exchangeRate = currency.exchange_rate_to_reference ?? currency.exchange_rate ?? "";
  const flags = resolveFlags(currency, baseURL);
  const mappedCountries = countries
    .map((country) => {
      const flag_icon =
        resolveFlagUrl(country?.flag_icon, baseURL) || flagcdnFromCode(country?.code) || null;
      if (!flag_icon) return null;
      return { ...country, flag_icon };
    })
    .filter(Boolean);
  const icon =
    currency.icon ||
    currency.flag_icon ||
    flags[0] ||
    flagcdnFromCode(currency.iso_country_code) ||
    null;

  return {
    ...currency,
    id: currency.id,
    name,
    code,
    symbol,
    currency_id: currency.currency_id ?? currency.currencyId ?? null,
    currency_name: name,
    currency_code: code,
    currency_symbol: symbol,
    exchange_rate: exchangeRate,
    exchange_rate_to_reference: exchangeRate,
    active: currency.active !== undefined ? currency.active : status !== "retired",
    status,
    rate_mode: currency.rate_mode === "corporate" ? "corporate" : "daily",
    is_reference: currency.is_reference === true || currency.reference === true,
    rate_updated_at: currency.rate_updated_at ?? currency.exchange_rate_updated_at ?? "",
    flags,
    ...(icon ? { icon } : {}),
    countries: mappedCountries,
  };
}

export function mapOperatingCurrencyRows(rows, baseURL = "") {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map((row) => mapOperatingCurrencyRow(row, baseURL));
}
